import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardImg,
  Col,
  Container,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNavigate } from "react-router-dom";
import { imageBaseApi } from "../../../utilis/imageHelper";
import DeleteModal from "../../../Components/Common/DeleteModal";
import WidgetService from "../../../services/KangarooServices/Intelligence/WidgetService";
import { getLoggedInUser } from "../../../helpers/fakebackend_helper";
import toast from "react-hot-toast";
import noimage from "../../../assets/kangaroo/noimage.png";
import Select from "react-select";


const Widget = () => {
  const navigate = useNavigate();
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const authUser = getLoggedInUser();
  const [pageSize, setPageSize] = useState(12);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    isOnshore: null,
    name: null,
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [data, setData] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});

  // Debounce filter input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  // Fetch widget data
  const fetchData = async (page = 1, size = pageSize) => {
    try {
      const response = await WidgetService.getList({
        ...debouncedFilters,
        page,
        pageSize: size,
      });
      setData(response?.data || []);
      setTotalRows(response?.totalRecords || 0);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize);
  }, [currentPage, pageSize, debouncedFilters]);

  // Delete modal functions
  const toggleDelete = () => setIsOpenDelete(!isOpenDelete);

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await WidgetService.delete(selectedDeleteItem.id);
        fetchData(currentPage, pageSize);
        toast.success("Widget deleted successfully");
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to delete widget");
      } finally {
        toggleDelete();
        setSelectedDeleteItem(null);
      }
    }
  };

  const toggleReadMore = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Pagination helpers
  const totalPages = Math.ceil(totalRows / pageSize);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const maxPagesToShow = 7;
    const pages = [];
    let startPage = Math.max(currentPage - 3, 1);
    let endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);
    startPage = Math.max(endPage - maxPagesToShow + 1, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Widgets" pageTitle="Widget Setup" />

        <Row className="mb-3">
          <Col lg={4}>
            <Button
              color="success"
              onClick={() => navigate("/widgets/create")}
            >
              <i className="bi bi-plus-lg"></i> Add Widget
            </Button>
          </Col>

          <Col lg={8}>
            <Input
              type="text"
              placeholder="Search by name"
              value={filters.name || ""}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </Col>
        </Row>

        <Row xs="1" md="2" lg="4" className="g-3">
          {data?.map((item) => {
            const maxLength = 150;
            const plainText = item?.description?.replace(/<[^>]*>/g, "") || "";
            const isLong = plainText.length > maxLength;
            const isExpanded = expandedItems[item.id];
            const shortText = plainText.slice(0, maxLength) + (isLong ? "..." : "");

            return (
              <Col key={item.id} lg={4}>
                <Card className="h-100 shadow-sm border-0 rounded-4 small-card">
                  <div className="logo-wrapper">
                    <CardImg
                      top
                      src={item?.image?.url ? `${imageBaseApi}${item.image.url}` : noimage}
                      alt={item.fileName}
                      style={{ objectFit: "contain", width: "100%", height: "100%" }}
                    />
                  </div>
                  <CardBody className="py-2 px-3">
                    <p>{item?.name}</p>
                  </CardBody>
                  <CardFooter className="bg-transparent border-0 pt-0 pb-3 px-3">
                    <div className="d-flex gap-2 justify-content-end">
                      <Button
                        size="sm"
                        color="warning"
                        onClick={() => navigate(`/widgets/update/${item?.id}`)}
                      >
                        <i className="ri-edit-line"></i>
                      </Button>
                      <Button
                        size="sm"
                        color="danger"
                        onClick={() => handleDeleteClick(item)}
                      >
                        <i className="ri-delete-bin-5-line"></i>
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              </Col>
            );
          })}
        </Row>

        {totalPages > 0 && (
          <div className="d-flex justify-content-center my-4 sticky-bottom">
            <Pagination className="mb-0">
              <PaginationItem disabled={currentPage === 1}>
                <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
              </PaginationItem>

              {getPageNumbers().map((page) => (
                <PaginationItem key={page} active={currentPage === page}>
                  <PaginationLink onClick={() => handlePageChange(page)}>
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem disabled={currentPage === totalPages}>
                <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        )}

        <DeleteModal
          show={isOpenDelete}
          onCloseClick={toggleDelete}
          onDeleteClick={handleDelete}
        />
      </Container>
    </div>
  );
};

export default Widget;